import React, { useState } from 'react';
import styles from './ReusableContactForm.module.css';

function ReusableContactForm({
  title = "Get In Touch",
  showTitle = true,
  compact = false,
  onSuccess,
  onError,
  className = ""
}) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errors, setErrors] = useState({});

  // Auto-clear success messages after 5 seconds
  React.useEffect(() => {
    if (submitStatus && submitStatus.type === 'success') {
      const timer = setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [submitStatus]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear field error when user starts typing
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
    // Clear submit status when user starts making changes
    if (submitStatus && submitStatus.type === 'error') {
      setSubmitStatus(null);
    }
  };

  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'name':
        if (!value.trim()) {
          error = 'Name is required';
        } else if (value.trim().length < 2) {
          error = 'Name must be at least 2 characters long';
        } else if (value.trim().length > 100) {
          error = 'Name must be less than 100 characters';
        }
        break;
      case 'email':
        if (!value.trim()) {
          error = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = 'Please enter a valid email address';
        } else if (value.length > 254) {
          error = 'Email address is too long';
        }
        break;
      case 'phone':
        if (value.trim() && !/^[\+]?[1-9][\d]{0,15}$/.test(value.replace(/[\s\-\(\)]/g, ''))) {
          error = 'Please enter a valid phone number';
        }
        break;
      case 'subject':
        if (value.trim() && value.trim().length > 200) {
          error = 'Subject must be less than 200 characters';
        }
        break;
      case 'message':
        if (!value.trim()) {
          error = 'Message is required';
        } else if (value.trim().length < 10) {
          error = 'Message must be at least 10 characters long';
        } else if (value.trim().length > 2000) {
          error = 'Message must be less than 2000 characters';
        }
        break;
      default:
        break;
    }

    return error;
  };

  const handleBlur = (e) => {
    // Remove blur validation - errors will only show on form submit
    // This prevents premature error highlighting when users are still filling out the form
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files.length > 1) {
      setSubmitStatus({ type: 'error', message: 'Please select only one file at a time.' });
      return;
    }

    if (files.length > 0) {
      const file = files[0];
      const fileError = validateFile(file);
      if (fileError) {
        setSubmitStatus({ type: 'error', message: fileError });
        return;
      }
      setSelectedFile(file);
      setSubmitStatus(null);
    }
  };

  const validateFile = (file) => {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif'
    ];

    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      return 'Please select a valid file type (PDF, DOC, DOCX, JPG, PNG, GIF)';
    }

    if (file.size > maxSize) {
      return 'File size exceeds the maximum limit of 10 MB';
    }

    return null;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileError = validateFile(file);
      if (fileError) {
        setSubmitStatus({ type: 'error', message: fileError });
        e.target.value = '';
        return;
      }
      setSelectedFile(file);
      setSubmitStatus(null);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    const fileInput = document.getElementById('reusable-attachment');
    if (fileInput) fileInput.value = '';
    setSubmitStatus(null);
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate all fields using the same validation logic
    const nameError = validateField('name', formData.name);
    const emailError = validateField('email', formData.email);
    const phoneError = validateField('phone', formData.phone);
    const subjectError = validateField('subject', formData.subject);
    const messageError = validateField('message', formData.message);

    if (nameError) newErrors.name = nameError;
    if (emailError) newErrors.email = emailError;
    if (phoneError) newErrors.phone = phoneError;
    if (subjectError) newErrors.subject = subjectError;
    if (messageError) newErrors.message = messageError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('message', formData.message);

      if (selectedFile) {
        formDataToSend.append('attachment', selectedFile);
      }

      // Create an AbortController for request timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/contact`, {
        method: 'POST',
        body: formDataToSend,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        // Handle cases where response is not valid JSON
        result = { message: 'Server returned an invalid response' };
      }

      // Handle specific HTTP status codes
      if (response.status === 429) {
        setSubmitStatus({
          type: 'error',
          message: 'Too many requests. Please wait a moment before trying again.'
        });
        if (onError) {
          onError({ message: 'Rate limit exceeded', status: 429 });
        }
        return;
      }

      if (response.status >= 500) {
        setSubmitStatus({
          type: 'error',
          message: 'Server error. Please try again later or contact support if the problem persists.'
        });
        if (onError) {
          onError({ message: 'Server error', status: response.status });
        }
        return;
      }

      if (response.ok) {
        const successMessage = result.message || 'Message sent successfully!';
        setSubmitStatus({ type: 'success', message: successMessage });
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        setSelectedFile(null);
        setErrors({});

        // Reset file input
        const fileInput = document.getElementById('reusable-attachment');
        if (fileInput) fileInput.value = '';

        // Call success callback if provided
        if (onSuccess) {
          onSuccess(result);
        }
      } else {
        // Handle server-side validation errors
        if (result.errors && typeof result.errors === 'object') {
          // Server returned field-specific validation errors
          setErrors(result.errors);
          setSubmitStatus({ type: 'error', message: 'Please correct the errors below and try again.' });
        } else {
          // General server error
          const errorMessage = result.message || 'Failed to send message. Please try again.';
          setSubmitStatus({ type: 'error', message: errorMessage });
        }

        // Call error callback if provided
        if (onError) {
          onError(result);
        }
      }
    } catch (error) {
      let errorMessage = 'Network error. Please try again.';

      // Handle different types of network errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Unable to connect to server. Please check your internet connection and try again.';
      } else if (error.name === 'AbortError') {
        errorMessage = 'Request timed out. Please try again.';
      } else if (error.message) {
        errorMessage = `Network error: ${error.message}`;
      }

      setSubmitStatus({ type: 'error', message: errorMessage });

      // Call error callback if provided
      if (onError) {
        onError({ message: errorMessage, error });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerClasses = `${styles.formContainer} ${compact ? styles.compact : ''} ${className}`.trim();

  return (
    <div className={containerClasses} role="region" aria-labelledby={showTitle ? "contact-form-title" : undefined}>
      {showTitle && (
        <h2 id="contact-form-title" className={styles.formTitle}>{title}</h2>
      )}

      <form
        onSubmit={handleSubmit}
        className={styles.contactForm}
        noValidate
        aria-label={showTitle ? undefined : "Contact form"}
      >
        <fieldset className={styles.formFieldset}>
          <legend className={styles.srOnly}>Contact Information</legend>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="reusable-name">
                Name <span aria-label="required">*</span>
              </label>
              <input
                type="text"
                id="reusable-name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                placeholder="Enter your full name"
                className={`${styles.formInput} ${errors.name ? styles.error : ''}`}
                aria-describedby={errors.name ? "name-error name-hint" : "name-hint"}
                aria-invalid={errors.name ? 'true' : 'false'}
                autoComplete="name"
              />
              <div id="name-hint" className={styles.srOnly}>
                Required field. Enter your full name.
              </div>
              {errors.name && (
                <span id="name-error" className={styles.errorMessage} role="alert" aria-live="polite">
                  {errors.name}
                </span>
              )}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="reusable-email">
                Email <span aria-label="required">*</span>
              </label>
              <input
                type="email"
                id="reusable-email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                placeholder="Enter your email address"
                className={`${styles.formInput} ${errors.email ? styles.error : ''}`}
                aria-describedby={errors.email ? "email-error email-hint" : "email-hint"}
                aria-invalid={errors.email ? 'true' : 'false'}
                autoComplete="email"
              />
              <div id="email-hint" className={styles.srOnly}>
                Required field. Enter a valid email address.
              </div>
              {errors.email && (
                <span id="email-error" className={styles.errorMessage} role="alert" aria-live="polite">
                  {errors.email}
                </span>
              )}
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="reusable-phone">Phone</label>
              <input
                type="tel"
                id="reusable-phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter your phone number"
                className={`${styles.formInput} ${errors.phone ? styles.error : ''}`}
                aria-describedby={errors.phone ? "phone-error phone-hint" : "phone-hint"}
                aria-invalid={errors.phone ? 'true' : 'false'}
                autoComplete="tel"
              />
              <div id="phone-hint" className={styles.srOnly}>
                Optional field. Enter your phone number.
              </div>
              {errors.phone && (
                <span id="phone-error" className={styles.errorMessage} role="alert" aria-live="polite">
                  {errors.phone}
                </span>
              )}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="reusable-subject">Subject</label>
              <input
                type="text"
                id="reusable-subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter the subject of your inquiry"
                className={`${styles.formInput} ${errors.subject ? styles.error : ''}`}
                aria-describedby={errors.subject ? "subject-error subject-hint" : "subject-hint"}
                aria-invalid={errors.subject ? 'true' : 'false'}
              />
              <div id="subject-hint" className={styles.srOnly}>
                Optional field. Enter the subject of your inquiry.
              </div>
              {errors.subject && (
                <span id="subject-error" className={styles.errorMessage} role="alert" aria-live="polite">
                  {errors.subject}
                </span>
              )}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="reusable-message">
              Message <span aria-label="required">*</span>
            </label>
            <textarea
              id="reusable-message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              onBlur={handleBlur}
              rows="5"
              required
              placeholder="Please describe your inquiry or project details..."
              className={`${styles.formTextarea} ${errors.message ? styles.error : ''}`}
              aria-describedby={errors.message ? "message-error message-hint" : "message-hint"}
              aria-invalid={errors.message ? 'true' : 'false'}
            />
            <div id="message-hint" className={styles.srOnly}>
              Required field. Minimum 10 characters, maximum 2000 characters.
            </div>
            {errors.message && (
              <span id="message-error" className={styles.errorMessage} role="alert" aria-live="polite">
                {errors.message}
              </span>
            )}
          </div>
        </fieldset>

        <fieldset className={styles.formFieldset}>
          <legend className={styles.srOnly}>File Attachment</legend>

          <div className={`${styles.formGroup} ${styles.attachmentGroup}`}>
            <label htmlFor="reusable-attachment">Attachment (optional)</label>
            {!selectedFile ? (
              <>
                <div
                  className={styles.fileDropZone}
                  onClick={() => document.getElementById('reusable-attachment').click()}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      document.getElementById('reusable-attachment').click();
                    }
                  }}
                  aria-label="Click to select a file or drag and drop. Supported formats: PDF, DOC, DOCX, JPG, PNG, GIF. Maximum size: 10MB"
                  aria-describedby="file-upload-hint"
                >
                  <span className={styles.fileDropText}>Click to select a file or drag and drop</span>
                  <span className={styles.fileDropHint}>PDF, DOC, DOCX, JPG, PNG, GIF (Max 10MB)</span>
                </div>
                <div id="file-upload-hint" className={styles.srOnly}>
                  Optional file attachment. Supported formats: PDF, DOC, DOCX, JPG, PNG, GIF. Maximum file size: 10 megabytes.
                </div>
                <input
                  type="file"
                  id="reusable-attachment"
                  name="attachment"
                  onChange={handleFileChange}
                  className={styles.hiddenFileInput}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                  aria-label="File attachment input"
                />
              </>
            ) : (
              <div className={styles.selectedFileContainer} role="group" aria-labelledby="selected-file-label">
                <div className={styles.selectedFileInfo}>
                  <span id="selected-file-label" className={styles.selectedFileName}>
                    Selected file: {selectedFile.name}
                  </span>
                  <span className={styles.selectedFileSize} aria-label={`File size: ${(selectedFile.size / 1024 / 1024).toFixed(2)} megabytes`}>
                    ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                </div>
                <button
                  type="button"
                  onClick={removeFile}
                  className={styles.removeFileButton}
                  aria-label={`Remove selected file: ${selectedFile.name}`}
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        </fieldset>

        <button
          type="submit"
          disabled={isSubmitting}
          className={styles.submitButton}
          aria-describedby={submitStatus ? "submit-status" : undefined}
          aria-label={isSubmitting ? 'Sending message, please wait' : 'Send message'}
        >
          {isSubmitting && (
            <div className={styles.loadingSpinner} aria-hidden="true" role="presentation"></div>
          )}
          <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
        </button>

        {submitStatus && (
          <div
            id="submit-status"
            className={`${styles.statusMessage} ${styles[submitStatus.type]}`}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
          >
            {submitStatus.message}
          </div>
        )}
      </form>
    </div>
  );
}

export default ReusableContactForm;